import SaAlgorithm = ToroEnums.SaAlgorithm;

import * as moment from 'moment';
import { AppInjector } from '../../../demo/demo.module';
import { DemoModeMockDataService } from '../../../demo/demo-mode-mock-data.service';
import { environment } from '../../../../environments/environment';
import { SiteManagerService } from '../../site/site-manager.service';
import { SpatialAdjustUtil } from '../../../spatial-adjust/utils/spatial-adjust.util';
import { ToroEnums } from '../../../common/enumerations/toro.enums';

export interface ISaEntity {
    id: string;
    enabled: boolean;
}

export class SaArea implements ISaEntity {
    constructor(json: any = null, siteManager: SiteManagerService) {
        if (json) {
            Object.assign(this, json);

            if (json.holes) {
                this.holes = json.holes.map((h: SaHole) => new SaHole(h, siteManager));

                if (this.holes && this.holes.length > 0) {
                    this.avgMoisture = +(this.holes.reduce((sum, hole) => sum + hole.avgMoisture, 0) / this.holes.length).toFixed(1);
                    this.avgTargetVwc = +(this.holes.reduce((sum, hole) => sum + hole?.avgTargetVwc, 0) / this.holes.length).toFixed(1);
                    this.lowMoisture = Math.min(...this.holes.map(hole => hole.lowMoisture));
                    this.highMoisture = Math.max(...this.holes.map(hole => hole.highMoisture));

                    const latestScan = this.holes.reduce((latest, current) => {
                        return latest.lastScanDate > current.lastScanDate ? latest : current;
                    });
                    this.lastScan = latestScan.lastScan;

                    // Check if any station in this hole has an estimated moisture value.
                    this.hasEstimatedChildren = !!this.holes.some(hole => hole.hasEstimatedChildren);
                }
            }
        }
    }

    id: string;
    name: string;
    enabled = false;
    selected: boolean = false;
    orgId: number;
    hasEstimatedChildren: boolean = false; // Indicates if any holes in this area have estimated moisture values.
    holes: SaHole[];

    avgMoisture = 0;
    avgTargetVwc = 0;
    highMoisture = 0;
    lowMoisture = 0;
    lastScan = '--';

    get rowSelected(): boolean | null {
        if (this.holes.every(hole => hole.rowSelected)) { return true; }
        if (this.holes.some(hole => hole.rowSelected != null)) { return false; }
        return null;
    }
}

// Hole
export class SaHole implements ISaEntity {
    constructor(json: any = null, siteManager: SiteManagerService) {
        if (json) {
            Object.assign(this, json);

            if (json.stations) {
                this.stations = json.stations.map((s: SaStation) => new SaStation(s, siteManager));

                if (this.stations && this.stations.length > 0) {
                    const validStations = this.stations.filter(station => station.isScanCurrent || station.isEstimated);
                    this.avgMoisture = validStations.length > 0
                        ? +(
                            validStations.reduce((sum, station) => {
                                if (station.isScanCurrent) {
                                    return sum + (station?.latestScan?.moistureMean ?? 0);
                                } else if (station.isEstimated) {
                                    return sum + (station?.latestAdjustment?.modelVwc ?? 0);
                                }
                                return sum;
                            }, 0) / validStations.length
                        ).toFixed(1)
                        : 0;

                    // Calculate the average target VWC across stations, ensuring we count each unique station name only once. The Target VWC for a station is
                    // set one time for the pair/group. We don't want to skew the average by including the value for each member of a pair/group.
                    const uniqueStations = Array.from(new Map(this.stations.filter(s => s?.name != null).map(s => [s.name, s])).values());
                    this.avgTargetVwc = +(uniqueStations.reduce((sum, s) => sum + (s?.targetVwcPercent ?? 0), 0) / uniqueStations.length).toFixed(1);

                    const latestScan = this.stations.reduce((latest, current) => {
                        return latest.latestScan.scanDateAsDate > current.latestScan.scanDateAsDate ? latest : current;
                    });
                    this.lastScan = latestScan.latestScan.scanDate;
                    this.lastScanDate = latestScan.latestScan.scanDateAsDate;

                    this.lowMoisture = Math.min(...this.stations.filter(station => station.isScanCurrent).map(station => station.latestScan.moistureMean));
                    this.highMoisture = Math.max(...this.stations.filter(station => station.isScanCurrent).map(station => station.latestScan.moistureMean));

                    // Check if any station in this hole has an estimated moisture value.
                    this.hasEstimatedChildren = !!this.stations.some(station => station.isEstimated)
                }
            }
        }
    }

    id: string;
    number: number;
    enabled = false;
    selected: boolean = false;
    hasEstimatedChildren: boolean = false;  // Indicates if any stations in this hole have estimated moisture values.
    stations: SaStation[];
    avgMoisture = 0;
    avgTargetVwc = 0;
    highMoisture = 0;
    lowMoisture = 0;
    lastScan = '--';
    lastScanDate: Date;

    get rowSelected(): boolean | null {
        if (this.stations.every(station => station.rowSelected)) { return true; }
        if (this.stations.some(station => station.rowSelected)) { return false; }
        return null;
    }
}

// Station
export class SaStation implements ISaEntity {
    private demoModeMockDataService: DemoModeMockDataService;

    constructor(json: any = null, siteManager: SiteManagerService) {
        if (json) {
            Object.assign(this, json);

            this.siteManager = siteManager;

            if (json.latestScan) this.latestScan = new SaScan(json.latestScan);
            if (json.latestAdjustment) this.latestAdjustment = new SaVwcAdjustment(json.latestAdjustment);
            if (json.userPercentAdjust != null) { this.userPercentAdjust = json.userPercentAdjust; }
            if (json.lastPushUtc != null) { this.lastPushUtc = moment.utc(json.lastPushUtc).toDate(); }
        }

        if (environment.isDemoMode) {
            this.demoModeMockDataService = AppInjector.get(DemoModeMockDataService);
            return;
        }
    }

    id: string;
    name: string;
    suggestedAdjAmtPercent: number
    userPercentAdjust: number;
    currentAdjAmtInches: number;
    currentAdjAmtPercent: number;
    stationAdjustId: number;
    targetVwcPercent: number;
    et: number;
    enabled = false;
    selected: boolean = false;
    rowSelected: boolean = false;
    latitude: number;
    longitude: number;
    radiusInMeters: number;             // In meters
    latestScan: SaScan;
    latestAdjustment: SaVwcAdjustment;
    submitChange = true;
    lastPushUtc: Date;
    soilFactorPct: number;

    private siteManager: SiteManagerService;

    get isScanCurrent() {
        if (this.latestScan == null) { return false; }

        // Demo Mode will be assumed if this.siteManager is null.
        const offsetSeconds = this.siteManager?.offsetFromUtcInSeconds ?? 0;
        return !environment.isDemoMode
            ? SpatialAdjustUtil.isScanCurrent(this.latestScan.scanDate, offsetSeconds)
            // TODO - Spatial Adjust Demo Support. Set the "Current Scan Date" to the date matching our static data.
            : this.latestScan.scanDate == this.demoModeMockDataService.spatialAdjustCurrentScanDate;
    }

    get isEstimated() {
        // Demo Mode will be assumed if this.siteManager is null.
        const spatialAdjustAlgo = this.siteManager?.spatialAdjustAlgo ?? SaAlgorithm.DeltaPlusTodayEt;
        return !this.isScanCurrent && spatialAdjustAlgo == SaAlgorithm.DeltaPlusTodayEt;
    }

    // This is used. This is what is returned, by convention, in a JSON.stringify() call.
    toJSON() {
        return {
            id: this.id,
            name: this.name,
            suggestedAdjAmtPercent: this.suggestedAdjAmtPercent,
            userPercentAdjust: this.userPercentAdjust,
            currentAdjAmtInches: this.currentAdjAmtInches,
            currentAdjAmtPercent: this.currentAdjAmtPercent,
            stationAdjustId: this.stationAdjustId,
            targetVwcPercent: this.targetVwcPercent,
            et: this.et,
            enabled: this.enabled,
            selected: this.selected,
            rowSelected: this.rowSelected,
            latitude: this.latitude,
            longitude: this.longitude,
            radiusInMeters: this.radiusInMeters,
            latestScan: this.latestScan,
            latestAdjustment: this.latestAdjustment,
            submitChange: this.submitChange,
            lastPushUtc: this.lastPushUtc
        }
    }
}

export class SaScan {
    constructor(json: any = null) {
        if (json) {
            Object.assign(this, json);

            if (json.scanDate) this.scanDateAsDate = new Date(json.scanDate);
        }
    }

    scanDate: string;
    scanDateAsDate: Date;
    moistureMean: number;
}

export class SaVwcAdjustment {

    constructor(json: any = null) {
        if (json) {
            Object.assign(this, json);

            if (json.scanDateCourseLocal) this.scanDateCourseLocal = new Date(json.scanDateCourseLocal);
            if (json.dateCourseLocal) this.dateCourseLocal = new Date(json.dateCourseLocal);
        }
    }

    id: number;
    orgId: number;
    stationName: string;
    scanDateCourseLocal: Date;
    dateCourseLocal: Date;
    avgMeasuredVwc: number;
    targetVwc: number;
    modelVwc: number;
    deltaVwc: number;

    // Today's ET im millimeters
    etMm: number;

    // Today's applied water amount in millimeters
    amountMm: number

    // The maximum amount of water in millimeters that can be applied.
    maxAmountMm: number;

    // Today's applied amount compared to max amount
    appliedToMax: number;

    predictedResult: number;

    // Today's Suggested Percent Adjust
    suggestedPercentAdjust: number;

    // REFERENCE AMOUNT used in Lynx for stations being managed by spatial adjust.
    referenceAmountMm: number;
}
